import React, { useState, FunctionComponent } from "react";
import {
  Card,
  Typography,
  Divider,
  Grid,
  TextField,
  Button,
  Theme,
  createStyles,
} from "@material-ui/core";
import { Formik, Form, ErrorMessage } from "formik";
import { useStore } from "../../store";
import * as yup from "yup";
import { SnackBar } from "../SnackBar";
import makeStyles from "@material-ui/core/styles/makeStyles";
import { useUsersStyles } from "./useUsersStyles";
import { api } from "../../api";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      position: "relative",
      overflowX: "auto",
    },
  })
);
const signupSchema = yup.object().shape({
  newPassword: yup
    .string()
    .min(2, "Too Short!")
    .max(70, "Too Long!")
    .required("Password is required"),

  confirmPassword: yup
    .string()
    .required("confirm password is required")
    .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

type resetPasswordParams = {
  resetPasswordId: string;
  storeList: string;
  goBackToList: () => void;
  goBack: () => void;
  storeUserData: Array<any>;
  posUserData: Array<any>;
};
export const ResetPassword: FunctionComponent<resetPasswordParams> = ({
  resetPasswordId,
  storeList,
  goBackToList,
  goBack,
  storeUserData,
  posUserData,
}) => {
  const classStyle = useStyles();
  const classes = useUsersStyles();
  const [user] = useStore("auth", "user");
  const [roles] = useStore("rolesForUser");
  const [snackbarColor, setSnackbarColor] = useState<string>("#d50000");
  const [userStatus, setUserStatus] = useState<string>("");

  const roleList =
    roles &&
    roles
      .filter((r: any) => user.roles.includes(r.id))
      .map(({ name }: any) => name.toLowerCase());

  const resetPassword = async (values: any) => {
    try {
      if (storeList === "store") {
        const current_pass_code =
          storeUserData && storeUserData !== undefined
            ? Object.values(storeUserData && storeUserData).find(
                (value: any) => value.id === resetPasswordId
              ).pass_code
            : "";
        const current_salt =
          storeUserData && storeUserData !== undefined
            ? Object.values(storeUserData && storeUserData).find(
                (value: any) => value.id === resetPasswordId
              ).salt
            : "";

        const current_password = values.currentPassword;
        const new_password = values.newPassword;
        const result = await api.post(
          `pos-users/verifyPassword`,
          {
            old_password: current_password,
            pass_code: current_pass_code,
            salt: current_salt,
            new_password: new_password,
          },
          {}
        );
        if (result.status===true) {
          const updatePassword = {
            pass_code: result.updated_pwd,
          };
          await api.patch(
            `stores/${user.store_id}/users/${resetPasswordId}/reset-password`,
            {},
            updatePassword
          );
          setSnackbarColor("green");
          setUserStatus("Password successfully reset");
          setTimeout(() => {
            goBackToList();
          }, 3000);
        } else {
          setSnackbarColor("red");
          setUserStatus("Password reset failed");
        }
      } else {
        const current_password =
          posUserData && posUserData !== undefined
            ? Object.values(posUserData && posUserData).find(
                (value: any) => value.id === resetPasswordId
              ).pass_code
            : "";
        const updatePassword = {
          pass_code: values.newPassword,
        };
        if (user.is_super_admin || roleList.includes("manager")) {
          await api.patch(
            `pos-users/reset-password/${resetPasswordId}`,
            {},
            updatePassword
          );
          setSnackbarColor("green");
          setUserStatus("Password successfully reset");
          setTimeout(() => {
            goBackToList();
          }, 3000);
        } else {
          if (current_password === values.currentPassword) {
            await api.patch(
              `pos-users/reset-password/${resetPasswordId}`,
              {},
              updatePassword
            );
            setSnackbarColor("green");
            setUserStatus("Password successfully reset");
            setTimeout(() => {
              goBackToList();
            }, 3000);
          } else {
            setSnackbarColor("red");
            setUserStatus("Password reset failed");
          }
        }
      }
    } catch (error: any) {
      setSnackbarColor("red");
      setUserStatus(error.error);
    }
  };
  const initialValues = {
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  };
  const onCloseSnackbar = () => {
    setUserStatus("");
  };
  return (
    <div className={classStyle.root}>
      <SnackBar
        status={userStatus}
        onCloseError={onCloseSnackbar}
        color={snackbarColor}
      />
      <Card className={classes.passwordCardStyle}>
        <Typography className={classes.passwordHeading}>
          Change Password
        </Typography>
        <Divider />
        <Formik
          enableReinitialize
          validationSchema={signupSchema}
          initialValues={initialValues}
          onSubmit={(values, { resetForm }) => {
            resetPassword(values);
          }}
        >
          {({ values, handleChange, setFieldValue, resetForm }) => (
            <Form>
              <Grid container direction="row">
                <Grid item xs>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      {storeList === "pos" &&
                      (user.is_super_admin ||
                        roleList.includes("manager")) ? null : (
                        <Grid
                          container
                          direction="row"
                          className={classes.passwordStyle}
                        >
                          <Grid item xs={4}>
                            <Typography className={classes.passwordTextStyle}>
                              Current Password
                            </Typography>
                          </Grid>
                          <Grid item xs={8}>
                            <TextField
                              variant="outlined"
                              name="currentPassword"
                              type="password"
                              value={values.currentPassword}
                              onChange={handleChange}
                              fullWidth={true}
                            />
                            <ErrorMessage name="currentPassword">
                              {(msg) => (
                                <div style={{ color: "red" }}>{msg}</div>
                              )}
                            </ErrorMessage>
                          </Grid>
                        </Grid>
                      )}
                      <Grid
                        container
                        direction="row"
                        className={classes.passwordStyle}
                      >
                        <Grid item xs={4}>
                          <Typography className={classes.passwordTextStyle}>
                            New Password
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            variant="outlined"
                            name="newPassword"
                            type="password"
                            value={values.newPassword}
                            onChange={handleChange}
                            fullWidth={true}
                          />
                          <ErrorMessage name="newPassword">
                            {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                          </ErrorMessage>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        direction="row"
                        className={classes.passwordStyle}
                      >
                        <Grid item xs={4}>
                          <Typography className={classes.passwordTextStyle}>
                            Confirm Password
                          </Typography>
                        </Grid>
                        <Grid item xs={8}>
                          <TextField
                            variant="outlined"
                            name="confirmPassword"
                            type="password"
                            value={values.confirmPassword}
                            onChange={handleChange}
                            fullWidth={true}
                          />
                          <ErrorMessage name="confirmPassword">
                            {(msg) => <div style={{ color: "red" }}>{msg}</div>}
                          </ErrorMessage>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        spacing={2}
                        direction="row"
                        className={classes.passwordStyle}
                      >
                        <Grid item xs={4}>
                          {" "}
                        </Grid>
                        <Grid item xs={4} sm="auto">
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            size="large"
                          >
                            <Typography>Change</Typography>
                          </Button>
                        </Grid>
                        <Grid item xs={4} sm="auto">
                          <Button
                            variant="contained"
                            color="primary"
                            type="reset"
                            size="large"
                          >
                            <Typography>Reset</Typography>
                          </Button>
                        </Grid>
                        <Grid item xs={4} sm="auto">
                          <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            onClick={goBack}
                          >
                            <Typography>Cancel</Typography>
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Card>
    </div>
  );
};
